// BUILD-046 tasks 8.16 / 8.23 / 8.24 (supporting model), rebuilt in correction cycle 1bb88b3e (stage B1, items N-items and F2): the "Requests I issued" panel
// (design "Group / Shared view and Take task"; Req 5.9, 5.11, 5.19, 4.28).
//
// WHERE THE ENTRIES COME FROM. The SERVER is the source of truth: `GET /work/issued` lists the requests the resolved Caller issued, newest first, in pages
// (an opaque `next` cursor), whatever browser session issued them. The list therefore survives a reload and is never another Caller's. Each entry is one
// request:
//   `set`   (mode everyone) : the aggregate "N of M" as the server computes it, the readable members and the requester (the `set` view of
//                             GET /work/sets/{requestId}). An assignment ended by a removal or a cancellation is NOT counted in N and is reported apart, never
//                             as completed (Req 5.19). The issuer actions of task 8.23 (issuerActionsModel.ts) work on it.
//   `queue` (mode anyone)   : one shared task (`item`), who took it and, when the owner has left the group, the server's owner-left text exactly as given.
//   `direct`(mode direct)   : one task assigned to one person (`item`).
// The session is only an optimistic HINT: a request just issued from the Assign task dialog is shown at once (`sessionOnly`) and the next read of the server
// list replaces it. If the server cannot give the list (an older server answers 404, or any failure) the hint stays so the Caller sees what was just sent,
// but the list itself says "unavailable" or what failed: it is never an empty list. Nothing here is attributed to a Caller by the browser: a Caller change
// clears the entries at once and the first page is read again for the new Caller.
//
// A figure that cannot be read is "Unavailable" with a Retry for that entry, never "0 of 0". An answer for a request that began under an older Caller epoch
// is ignored (Req 2.7). Framework-free (the Group / Shared view subscribes through useSyncExternalStore). Erasable TypeScript; runtime imports: the wording
// helper of taskRowModel and the paged-list engine.

import { createPagedList } from './pagedListController';
import { describePaged } from './pagedListModel';
import type { PagedState } from './pagedListModel';
import { queueWording } from './taskRowModel';
// BUILD-046 J1a: the Customer / Job of a request (pure module). An Everyone request carries it on the entry, any other on its task.
import { asJobShown, jobOfIssuedEntry } from './jobDisplayModel';
import { personDisplay, stripTestMarker } from './displayNames';
import type { JobShown } from './jobDisplayModel';
// BUILD-046 J1a (Customer association): the Customer of a request (pure module), beside its Job. An Everyone request carries it on the entry, any other on its task.
import { asCustomerShown, customerOfIssuedEntry } from './customerDisplayModel';
import type { CustomerShown } from './customerDisplayModel';

export interface IssuedFigure {
  n: number;
  m: number;
  label: string;
  ended: number;
  setCancelled: boolean;
  allDone: boolean;
}

export type IssuedPhase = 'loading' | 'ready' | 'error';
export type IssuedKind = 'set' | 'queue' | 'direct';

export interface IssuedPerson {
  employeeId: string;
  displayName: string;
}

/** Owner correction 2026-10-24 (item 2): the latest progress note of a task with its author, the activity date it names (may be backdated) and the time it was recorded (its own value). */
export interface IssuedProgress {
  text: string;
  actorName: string;
  activityDate: string;
  recordedAt: string;
}

/** One RECORDED change of a task's Task due date: `to` is '' when the date was cleared. */
export interface IssuedDueChange {
  from: string;
  to: string;
  reason: string;
  actorName: string;
  recordedAt: string;
  byRequester: boolean;
}

/** What the requester may read of the work on a task they issued, as the server sent it for a task shown in full (null otherwise). Only recorded facts: nothing is inferred. */
export interface IssuedInsight {
  progress: IssuedProgress | null;
  /** Newest first. */
  dueChanges: IssuedDueChange[];
  /** True when the history is longer than the server read: older changes are in History. */
  incomplete: boolean;
  nextAction: string;
  followUpDate: string;
}

/** One readable assignment of an Everyone set. An ended one (removed or cancelled) is never completed and never counted in N. */
export interface IssuedMember {
  employeeId: string;
  displayName: string;
  itemId: string;
  ended: boolean;
  /** Why an ended assignment ended; null while it is live. */
  outcome: 'removed' | 'cancelled' | null;
  /** The live assignment's status (active, waiting, blocked, completed); null for an ended one. */
  status: string | null;
  /**
   * Owner direction 2026-10-23: the priority and Task due date of THIS member's own task as the set read returned them ('' when the task has none, when the member ended, or when the task is shown in its
   * restricted form, which `restricted` says). They are what the screen aggregates for an Everyone row ("Varies" where the recipients differ); nothing here is computed from another member.
   */
  priority: string;
  dueDate: string;
  restricted: boolean;
  /** Owner correction 2026-10-24: THIS member's own next action and follow-up date, and its own recorded progress and due-date changes (null/'' when it has none or is shown in its restricted form). */
  nextAction: string;
  followUpDate: string;
  insight: IssuedInsight | null;
}

/** The part of a task the panel and its action controls read. */
export interface IssuedTask {
  id: string;
  title: string;
  status: string;
  assignmentKind: string;
  unclaimed: boolean;
  ownerEmployeeId: string;
  ownerName: string;
  /** Owner decision D8: the person who issued the task (the server keeps it even on a task shown in its restricted form). Edit task is offered to this person for an open task. */
  requesterEmployeeId: string;
  reassignmentNeeded: boolean;
  redacted: boolean;
  sourceGoverned: boolean;
  lifecycle: string;
  /** The server's owner-left texts, exactly as given (Req 4.28). */
  ownerLeft: string[];
  /** BUILD-046 1a: the task's own priority and Task due date as the read returned them ('' when it carried none or the task is shown in its restricted form), the server's overdue flag (only the boolean true counts) and why an assignment ended. */
  priority: string;
  dueDate: string;
  overdue: boolean;
  outcome: string;
  /**
   * Owner direction 2026-10-23: the people an unclaimed Anyone request was sent to by name (the server's `recipientSummary`), so the Assigned to cell can name them instead of saying "Not available".
   * Empty for a task with none and for a task shown in its restricted form.
   */
  recipients: IssuedPerson[];
  /** Owner correction 2026-10-24 (item 2): the task's next action and follow-up date as the read returned them, and what the requester may read of its recorded work (null when none or restricted). */
  nextAction: string;
  followUpDate: string;
  insight: IssuedInsight | null;
}

export interface IssuedEntry {
  requestId: string;
  kind: IssuedKind;
  /** The task (kinds `queue` and `direct`); null for a set. */
  taskId: string | null;
  title: string;
  /** The Work_Group names the request named, for the wording on the row. */
  groupNames: string[];
  /** When the server recorded the request (ISO instant), or null for a hint that the server has not listed yet. */
  createdAt: string | null;
  /** `server`: read from GET /work/issued. `session`: an optimistic hint from this page session, replaced by the next read of the server list. */
  source: 'server' | 'session';
  phase: IssuedPhase;
  figure: IssuedFigure | null;
  requestState: 'issued' | 'cancelled' | null;
  requester: IssuedPerson | null;
  members: readonly IssuedMember[];
  task: IssuedTask | null;
  /** The Customer / Job the request was issued for (BUILD-046 J1a), or null. It is a property of the request, so a refresh of the figure or the task keeps it. */
  job: JobShown | null;
  /** The Customer the request was issued for (BUILD-046 J1a), or null. It is a property of the request, so a refresh of the figure or the task keeps it. Independent of `job`. */
  customer: CustomerShown | null;
  error: string | null;
}

export interface IssuedState {
  /** The server's list, page by page. The only authoritative part. */
  list: PagedState<IssuedEntry>;
  /** Requests issued in this page session that the server list has not replaced yet, newest first. Never authoritative. */
  hints: readonly IssuedEntry[];
}

export const NOT_IN_FORM = 'The server answered, but not in the expected form.';
export const ISSUED_UNAVAILABLE_TEXT = 'Requests I issued are unavailable on this server';
export const ISSUED_EMPTY_TEXT = 'You have not issued any requests.';

export function initialIssuedState(epoch = 0): IssuedState {
  return { list: { phase: 'idle', epoch, seq: 0, items: [], next: null, loadingMore: false, moreError: null, error: null }, hints: [] };
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Reads the server's `insight` object of a task or a member. Null when it is absent or not that shape; nothing is made up. */
export function parseInsight(raw: unknown): IssuedInsight | null {
  if (!isRecord(raw)) return null;
  const latest = raw.latestProgress;
  const progress =
    isRecord(latest) && text(latest.text).trim() !== ''
      ? { text: text(latest.text), actorName: isRecord(latest.actor) ? text(latest.actor.name).trim() : '', activityDate: text(latest.activityDate), recordedAt: text(latest.recordedAt) }
      : null;
  const dueChanges: IssuedDueChange[] = [];
  if (Array.isArray(raw.dueChanges)) {
    for (const change of raw.dueChanges) {
      if (!isRecord(change) || text(change.from) === '') continue;
      dueChanges.push({
        from: text(change.from),
        to: text(change.to),
        reason: text(change.reason).trim(),
        actorName: isRecord(change.actor) ? text(change.actor.name).trim() : '',
        recordedAt: text(change.recordedAt),
        byRequester: change.capacity === 'requester',
      });
    }
  }
  return { progress, dueChanges, incomplete: raw.dueChangesIncomplete === true, nextAction: text(raw.nextAction), followUpDate: text(raw.followUpDate) };
}

function parseMembers(value: unknown): IssuedMember[] {
  if (!Array.isArray(value)) return [];
  const out: IssuedMember[] = [];
  for (const raw of value) {
    if (!isRecord(raw) || typeof raw.employeeId !== 'string' || raw.employeeId === '') continue;
    const ended = raw.ended === true;
    const insight = !ended && raw.redacted !== true ? parseInsight(raw.insight) : null;
    out.push({
      employeeId: raw.employeeId,
      displayName: text(raw.displayName).trim() === '' ? 'Unknown employee' : text(raw.displayName).trim(),
      itemId: text(raw.itemId),
      ended,
      outcome: ended ? (raw.outcome === 'removed' ? 'removed' : 'cancelled') : null,
      status: ended ? null : typeof raw.status === 'string' ? raw.status : null,
      priority: !ended && raw.redacted !== true ? text(raw.priority) : '',
      dueDate: !ended && raw.redacted !== true ? text(raw.dueDate) : '',
      restricted: !ended && raw.redacted === true,
      nextAction: insight === null ? '' : insight.nextAction,
      followUpDate: insight === null ? '' : insight.followUpDate,
      insight,
    });
  }
  return out;
}

/** Reads `{ set: { requestState, requester, figure: { n, m, label, ended, setCancelled, allDone }, members } }`. Returns null when it is not that shape. */
export function parseSetAnswer(body: unknown): { figure: IssuedFigure; requestState: 'issued' | 'cancelled'; requester: IssuedPerson | null; members: IssuedMember[] } | null {
  if (!isRecord(body)) return null;
  const set = body.set;
  if (!isRecord(set)) return null;
  const figure = set.figure;
  if (!isRecord(figure)) return null;
  const f = figure;
  if (!isCount(f.n) || !isCount(f.m) || f.n > f.m) return null;
  const requestState = set.requestState === 'cancelled' ? 'cancelled' : 'issued';
  const requester = isRecord(set.requester) && typeof set.requester.employeeId === 'string' && set.requester.employeeId !== '' ? { employeeId: set.requester.employeeId, displayName: text(set.requester.displayName) } : null;
  return {
    requestState,
    requester,
    members: parseMembers(set.members),
    figure: {
      n: f.n,
      m: f.m,
      // The label is recomputed from the numbers when the server's is missing; it is never made up from anything else.
      label: typeof f.label === 'string' && f.label.trim() !== '' ? f.label : `${f.n} of ${f.m}`,
      ended: isCount(f.ended) ? f.ended : 0,
      setCancelled: f.setCancelled === true || requestState === 'cancelled',
      allDone: f.allDone === true,
    },
  };
}

/** Reads `{ item }` of `GET /work/{id}`. Returns null when it is not a task. The owner-left texts are kept exactly as the server gave them. */
export function parseTaskAnswer(body: unknown): IssuedTask | null {
  if (!isRecord(body) || !isRecord(body.item)) return null;
  const item = body.item;
  const id = item.id;
  if (typeof id !== 'string' || id === '') return null;
  const ownerLeft: string[] = [];
  if (item.redacted !== true && Array.isArray(item.ownerLeft)) {
    for (const entry of item.ownerLeft) if (isRecord(entry) && typeof entry.text === 'string' && entry.text.trim() !== '') ownerLeft.push(entry.text);
  }
  const recipients: IssuedPerson[] = [];
  if (item.redacted !== true && Array.isArray(item.recipientSummary)) {
    for (const raw of item.recipientSummary) {
      if (isRecord(raw) && typeof raw.employeeId === 'string' && raw.employeeId !== '' && text(raw.name).trim() !== '') recipients.push({ employeeId: raw.employeeId, displayName: text(raw.name).trim() });
    }
  }
  return {
    id,
    title: text(item.title).trim() === '' ? 'Task' : text(item.title),
    status: text(item.status),
    assignmentKind: text(item.assignmentKind),
    unclaimed: item.unclaimed === true,
    ownerEmployeeId: text(item.ownerEmployeeId),
    ownerName: text(item.ownerName),
    requesterEmployeeId: text(item.requesterEmployeeId),
    reassignmentNeeded: item.reassignmentNeeded === true,
    redacted: item.redacted === true,
    sourceGoverned: item.sourceGoverned === true,
    lifecycle: text(item.lifecycle),
    ownerLeft,
    // A restricted (redacted) task carries neither: the server withholds them, and nothing is read from it here.
    priority: item.redacted === true ? '' : text(item.priority),
    dueDate: item.redacted === true ? '' : text(item.dueDate),
    overdue: item.redacted !== true && item.overdue === true,
    outcome: text(item.outcome),
    recipients,
    nextAction: item.redacted === true ? '' : text(item.nextAction),
    followUpDate: item.redacted === true ? '' : text(item.followUpDate),
    insight: item.redacted === true ? null : parseInsight(body.insight),
  };
}

function messageOf(thrown: unknown): string {
  if (thrown !== null && typeof thrown === 'object') {
    const t = thrown as { kind?: unknown; message?: unknown };
    if (t.kind === 'timeout') return 'There was no answer in time.';
    if (t.kind === 'network') return 'The server could not be reached.';
    if (typeof t.message === 'string' && t.message.trim() !== '') return t.message.trim();
  }
  return 'The figure could not be loaded.';
}

function cleanNames(names: readonly string[] | undefined): string[] {
  return (names ?? []).filter((n) => typeof n === 'string' && n.trim() !== '').map((n) => n.trim());
}

// ───────────────────────── entries ─────────────────────────

function blankEntry(requestId: string, kind: IssuedKind, taskId: string | null, title: string, groupNames: string[], createdAt: string | null, source: 'server' | 'session'): IssuedEntry {
  return { requestId, kind, taskId, title, groupNames, createdAt, source, phase: 'loading', figure: null, requestState: null, requester: null, members: [], task: null, job: null, customer: null, error: null };
}

const KIND_OF_MODE: Readonly<Record<string, IssuedKind>> = Object.freeze({ everyone: 'set', anyone: 'queue', direct: 'direct' });

/**
 * One entry of `GET /work/issued`: `{ requestId, mode, createdAt, title, groupNames?, set? | item? }`. The figure, members and requester (set) or the task
 * (item) come with the entry, so it is `ready` without another request. An entry whose set or item is not the documented shape is an `error` entry with
 * its own Retry (a re-read through the set or task route), never a figure of zero.
 */
export function entryFromServer(raw: unknown): IssuedEntry | null {
  if (!isRecord(raw) || typeof raw.requestId !== 'string' || raw.requestId === '') return null;
  const kind = KIND_OF_MODE[String(raw.mode)];
  if (kind === undefined) return null;
  const title = typeof raw.title === 'string' ? raw.title.trim() : '';
  const groupNames = cleanNames(Array.isArray(raw.groupNames) ? (raw.groupNames as string[]) : undefined);
  const createdAt = typeof raw.createdAt === 'string' && raw.createdAt !== '' && raw.createdAt !== '-' ? raw.createdAt : null;
  const itemId = isRecord(raw.item) && typeof raw.item.id === 'string' ? raw.item.id : raw.requestId;
  const entry: IssuedEntry = { ...blankEntry(raw.requestId, kind, kind === 'set' ? null : itemId, title, groupNames, createdAt, 'server'), job: jobOfIssuedEntry(raw), customer: customerOfIssuedEntry(raw) };
  if (kind === 'set') return entryLoaded(entry, { set: raw.set });
  return entryTaskLoaded(entry, { item: raw.item, insight: raw.insight });
}

/** The page of `GET /work/issued` as entries and the next cursor, or null when the answer is not the documented shape. */
export function parseIssuedPage(body: unknown): { entries: IssuedEntry[]; next: string | null } | null {
  if (!isRecord(body) || !Array.isArray(body.requests)) return null;
  const entries: IssuedEntry[] = [];
  for (const raw of body.requests) {
    const entry = entryFromServer(raw);
    if (entry === null) return null;
    entries.push(entry);
  }
  return { entries, next: typeof body.next === 'string' && body.next !== '' ? body.next : null };
}

export function entryRefreshing(entry: IssuedEntry): IssuedEntry {
  return { ...entry, phase: 'loading', error: null };
}

/** The set answer for an entry. */
export function entryLoaded(entry: IssuedEntry, body: unknown): IssuedEntry {
  const parsed = parseSetAnswer(body);
  return parsed === null
    ? { ...entry, phase: 'error', figure: null, requestState: null, requester: null, members: [], error: NOT_IN_FORM }
    : { ...entry, phase: 'ready', figure: parsed.figure, requestState: parsed.requestState, requester: parsed.requester, members: parsed.members, error: null };
}

/** The task answer for an entry. */
export function entryTaskLoaded(entry: IssuedEntry, body: unknown): IssuedEntry {
  const task = parseTaskAnswer(body);
  // A task read again carries its CURRENT title (an Edit task changes it), so the row follows an edit. A restricted task is shown in its minimal form and never replaces the title the list gave.
  return task === null ? { ...entry, phase: 'error', task: null, error: NOT_IN_FORM } : { ...entry, title: !task.redacted && task.title.trim() !== '' ? task.title : entry.title, phase: 'ready', task, error: null };
}

export function entryFailed(entry: IssuedEntry, thrown: unknown): IssuedEntry {
  return { ...entry, phase: 'error', figure: null, requestState: null, requester: null, members: [], task: null, error: messageOf(thrown) };
}

// ───────────────────────── the optimistic hint ─────────────────────────

export interface AcceptedRequest {
  requestId?: unknown;
  mode?: unknown;
  items?: unknown;
}

export interface TrackMeta {
  /** The Work_Group names the Caller selected (empty or missing when the request named no group). */
  groupNames?: readonly string[];
  /** The Customer / Job the Caller chose (BUILD-046 J1a), so the hint row shows it at once; the next read of the server list replaces it with the server's own. */
  job?: { jobId: string; label: string } | null;
  /** The Customer the Caller chose (BUILD-046 J1a), so the hint row shows it at once; the next read of the server list replaces it with the server's own. */
  customer?: { customerId: string; label: string } | null;
}

/**
 * An accepted request as a HINT, newest first: an Everyone request is a `set`, an Anyone request a `queue` task, a direct assignment a `direct` task. A
 * response that names no request, an unknown mode, or a request already in the hints adds nothing new. Returns the entry to read, or null.
 */
export function addHint(hints: readonly IssuedEntry[], response: AcceptedRequest | null | undefined, title: string, meta: TrackMeta = {}): { hints: readonly IssuedEntry[]; entry: IssuedEntry | null } {
  if (response === null || response === undefined || typeof response.requestId !== 'string' || response.requestId === '') return { hints, entry: null };
  const kind = KIND_OF_MODE[String(response.mode)];
  if (kind === undefined) return { hints, entry: null };
  const requestId = response.requestId;
  const existing = hints.find((e) => e.requestId === requestId);
  if (existing !== undefined) return { hints, entry: existing };
  let taskId: string | null = null;
  if (kind !== 'set') {
    const first: unknown = Array.isArray(response.items) ? response.items[0] : undefined;
    taskId = isRecord(first) && typeof first.id === 'string' && first.id !== '' ? first.id : requestId;
  }
  const entry: IssuedEntry = { ...blankEntry(requestId, kind, taskId, title.trim(), cleanNames(meta.groupNames), null, 'session'), job: asJobShown(meta.job), customer: asCustomerShown(meta.customer) };
  return { hints: [entry, ...hints], entry };
}

// ───────────────────────── what the panel shows ─────────────────────────

export type IssuedRowStatus = 'loading' | 'ready' | 'unavailable';

export interface IssuedRow {
  requestId: string;
  title: string;
  /** The title as it is SHOWN (and named for assistive technology): without the "(FICTIONAL)" test marker. `title` stays the stored text that search reads. */
  displayTitle: string;
  kind: IssuedKind;
  status: IssuedRowStatus;
  /** When the server recorded the request; null for a hint. */
  createdAt: string | null;
  /** True for a request just issued in this page session that the server list has not listed (yet). */
  sessionOnly: boolean;
  /** `2 of 3`, or null while loading, when unavailable and for a task (never `0 of 0`). */
  label: string | null;
  /** BUILD-046 1a: the set's figure as the server computed it (n done of m, ended apart, cancelled, all done); null for a task, while loading and when unavailable. */
  figure: IssuedFigure | null;
  /** Distinct outcomes of a set, none of them counted as completed. */
  note: string | null;
  error: string | null;
  canRetry: boolean;
  /** The Work_Group names the request named. */
  groupNames: string[];
  /** The same group names as they are SHOWN (without the "(FICTIONAL)" test marker). */
  groupLabels: string[];
  /** `Anyone currently in <Group> can take this.` for a queue task still waiting; the fixed-at-issue sentence for a set sent to a group. */
  wording: string | null;
  /** Where a task stands: `Waiting in the queue`, `Taken by <name>`, `Assigned to <name>`, `Completed`, `Cancelled`. The person is First name + last initial. Null for a set and while not loaded. */
  statusText: string | null;
  /** The same sentence with the person's full name, for the hover and focus text; null when it adds nothing to `statusText`. */
  statusFullText: string | null;
  /** The server's owner-left texts as given (only the "(FICTIONAL)" test marker of a group name is not shown). */
  ownerLeft: string[];
  reassignmentNeeded: boolean;
  /** The set's state, requester and readable members (the issuer actions read these). */
  requestState: 'issued' | 'cancelled' | null;
  requester: IssuedPerson | null;
  members: readonly IssuedMember[];
  /** The task (queue and direct), for the action controls. */
  task: IssuedTask | null;
  /** The Customer / Job the request was issued for (BUILD-046 J1a), or null. */
  job: JobShown | null;
  /** The Customer the request was issued for (BUILD-046 J1a, Customer association), or null. */
  customer: CustomerShown | null;
}

export function fixedAtIssueSentence(groupNames: readonly string[]): string | null {
  // The group names are SHOWN without the "(FICTIONAL)" test marker.
  const names = cleanNames(groupNames).map((name) => stripTestMarker(name)).filter((name) => name !== '');
  return names.length === 0 ? null : `Sent to ${names.join(', ')}. The recipients were fixed when it was sent; people who join the group later are not added.`;
}

/** Where a task stands. The owner is written First name + last initial (`form` 'short') or in full (`form` 'full'), without the "(FICTIONAL)" test marker. */
function statusTextOf(task: IssuedTask, kind: IssuedKind, form: 'short' | 'full' = 'short'): string {
  if (task.lifecycle === 'cancelled') return 'Cancelled';
  if (task.status === 'completed') return 'Completed';
  if (task.unclaimed) return 'Waiting in the queue';
  const person = personDisplay(task.ownerName);
  const name = form === 'short' ? person.short : person.full;
  if (kind === 'direct') return name === '' ? 'Assigned' : `Assigned to ${name}`;
  return name === '' ? 'Taken' : `Taken by ${name}`;
}

function rowOf(e: IssuedEntry): IssuedRow {
  const figure = e.figure;
  const notes: string[] = [];
  if (e.kind === 'set' && e.phase === 'ready' && figure !== null) {
    if (figure.setCancelled) notes.push('Request cancelled');
    else if (figure.allDone) notes.push('All done');
    if (figure.ended > 0) notes.push(`${figure.ended} removed or cancelled (not counted)`);
  }
  const task = e.kind !== 'set' && e.phase === 'ready' ? e.task : null;
  // A queue task that is waiting (or not read yet: it was unclaimed when issued) carries the live-queue wording; a taken one does not.
  const waiting = e.kind === 'queue' && (task === null ? e.phase === 'loading' : task.unclaimed && task.status !== 'completed' && task.lifecycle !== 'cancelled');
  const title = e.title === '' ? (task?.title ?? 'Task') : e.title;
  const statusText = task === null ? null : statusTextOf(task, e.kind);
  const statusFull = task === null ? null : statusTextOf(task, e.kind, 'full');
  return {
    requestId: e.requestId,
    title,
    displayTitle: stripTestMarker(title) !== '' ? stripTestMarker(title) : 'Task',
    kind: e.kind,
    status: e.phase === 'error' ? 'unavailable' : e.phase,
    createdAt: e.createdAt,
    sessionOnly: e.source === 'session',
    label: e.kind === 'set' && e.phase === 'ready' && figure !== null ? figure.label : null,
    figure: e.kind === 'set' && e.phase === 'ready' ? figure : null,
    note: notes.length > 0 ? notes.join('. ') : null,
    error: e.phase === 'error' ? e.error : null,
    canRetry: e.phase === 'error',
    groupNames: e.groupNames,
    groupLabels: e.groupNames.map((name) => stripTestMarker(name)).filter((name) => name !== ''),
    wording: e.kind === 'set' ? fixedAtIssueSentence(e.groupNames) : waiting ? queueWording({ unclaimed: true, groupRefs: e.groupNames.map((name) => ({ name })) }) : null,
    statusText,
    statusFullText: statusFull !== null && statusFull !== statusText ? statusFull : null,
    ownerLeft: task === null ? [] : task.ownerLeft.map((line) => stripTestMarker(line)).filter((line) => line !== ''),
    reassignmentNeeded: task !== null && task.reassignmentNeeded,
    requestState: e.kind === 'set' ? e.requestState : null,
    requester: e.kind === 'set' ? e.requester : null,
    members: e.kind === 'set' ? e.members : [],
    task,
    job: e.job,
    customer: e.customer,
  };
}

export type IssuedListStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

export interface IssuedView {
  rows: IssuedRow[];
  /** Said when the list was read and holds nothing. Never shown for a list that is loading, unavailable or failed. */
  emptyText: string;
  /** The state of the SERVER list (the hints are rows either way). */
  status: IssuedListStatus;
  /** Why the list is not shown: `Requests I issued are unavailable on this server`, or the failure heading and detail. Null when it is. */
  message: string | null;
  canRetry: boolean;
  /** "Load more" is offered, and the loaded page count is not the whole list. */
  canLoadMore: boolean;
  loadingMore: boolean;
  loadMoreError: string | null;
  /** How many requests are loaded, and whether that is every one of them (a search over them must say so when it is not). */
  loadedCount: number;
  complete: boolean;
}

export function describeIssued(state: IssuedState): IssuedView {
  const paged = describePaged(state.list, { label: 'requests I issued', emptyText: ISSUED_EMPTY_TEXT, unavailableText: ISSUED_UNAVAILABLE_TEXT });
  const hintRows = state.hints.map(rowOf);
  const base = { emptyText: ISSUED_EMPTY_TEXT, loadMoreError: null as string | null };
  switch (paged.kind) {
    case 'idle':
      return { ...base, rows: hintRows, status: 'idle', message: null, canRetry: false, canLoadMore: false, loadingMore: false, loadedCount: hintRows.length, complete: false };
    case 'loading':
      return { ...base, rows: hintRows, status: 'loading', message: null, canRetry: false, canLoadMore: false, loadingMore: false, loadedCount: hintRows.length, complete: false };
    case 'unavailable':
      return { ...base, rows: hintRows, status: 'unavailable', message: paged.text, canRetry: false, canLoadMore: false, loadingMore: false, loadedCount: hintRows.length, complete: false };
    case 'error':
      return { ...base, rows: hintRows, status: 'error', message: `${paged.heading}: ${paged.detail}`, canRetry: true, canLoadMore: false, loadingMore: false, loadedCount: hintRows.length, complete: false };
    case 'empty':
      return { ...base, rows: hintRows, status: 'ready', message: null, canRetry: false, canLoadMore: false, loadingMore: false, loadedCount: hintRows.length, complete: true };
    case 'data': {
      const rows = [...hintRows, ...paged.items.map(rowOf)];
      return { ...base, rows, status: 'ready', message: null, canRetry: false, canLoadMore: paged.canLoadMore, loadingMore: paged.loadingMore, loadMoreError: paged.moreError, loadedCount: rows.length, complete: paged.complete };
    }
  }
}

// ───────────────────────── the store ─────────────────────────

export interface IssuedPageQuery {
  limit?: number;
  cursor?: string | null;
}

export interface IssuedStoreDeps {
  getEpoch: () => number;
  /** `GET /work/issued`: the body of one page. */
  getIssued: (query: IssuedPageQuery) => Promise<unknown>;
  /** `GET /work/sets/{requestId}`. */
  getSet: (requestId: string) => Promise<unknown>;
  /** `GET /work/{id}` (a queue or direct entry's task). */
  getTask?: (taskId: string) => Promise<unknown>;
  /** True when a failure means the server does not serve the route (isRouteUnavailable). Default: never. */
  isUnavailable?: (thrown: unknown) => boolean;
  /** Entries per page; the server's own default when omitted. */
  pageSize?: number;
}

export interface IssuedStore {
  getState: () => IssuedState;
  subscribe: (listener: () => void) => () => void;
  /** Reads the first page when nothing has been requested yet (the panel calls it when it is shown). */
  ensureLoaded: () => Promise<void>;
  /** Reads the first page again (Retry, Refresh). The hints are replaced by what the server lists. */
  load: () => Promise<void>;
  /** Reads and appends the next page ("Load more"). */
  loadMore: () => Promise<void>;
  /** Called with an accepted Assign response: shows it at once as a hint and reads the server list again, which replaces the hint. */
  track: (response: AcceptedRequest | null | undefined, title: string, meta?: TrackMeta) => Promise<void>;
  /** Reads ONE entry again (its Retry) through the set or task route. */
  refresh: (requestId: string) => Promise<void>;
  /** Reads the server list again from its first page. */
  refreshAll: () => Promise<void>;
  /**
   * The answer of an accepted issuer action (add recipient, remove recipient, cancel). It carries the updated set when the server could read it;
   * otherwise the entry is read again. Nothing is changed locally by guessing.
   */
  applyChange: (requestId: string, body: unknown) => Promise<void>;
  /** A Caller change: everything is cleared at once (entries and hints), and the first page is read again for the new Caller when the list was in use. */
  callerChanged: (change?: CallerChange) => Promise<void>;
}

/** The part of the `caller-changed` event the store accepts. It is not used to attribute anything: the server resolves the Caller. */
export interface CallerChange {
  previousEmployeeId: string | null;
  currentEmployeeId: string | null;
}

export function createIssuedStore(deps: IssuedStoreDeps): IssuedStore {
  const list = createPagedList<IssuedEntry>({
    getEpoch: deps.getEpoch,
    keyOf: (entry) => entry.requestId,
    ...(deps.isUnavailable !== undefined ? { isUnavailable: deps.isUnavailable } : {}),
    fetchPage: async (cursor) => {
      const body = await deps.getIssued({ ...(deps.pageSize !== undefined ? { limit: deps.pageSize } : {}), cursor });
      const page = parseIssuedPage(body);
      if (page === null) throw Object.assign(new Error(NOT_IN_FORM), { kind: 'invalid-response' });
      return { items: page.entries, next: page.next };
    },
  });
  let hints: readonly IssuedEntry[] = [];
  let state: IssuedState = { list: list.getState(), hints };
  const listeners = new Set<() => void>();

  function publish(): void {
    const pagedNow = list.getState();
    // The server list replaces the hints as soon as it has been read. (It is cleared at the Caller change and while it loads; a hint added after
    // the reload began survives until that reload's answer arrives.)
    if (pagedNow.phase === 'ready' && hints.length > 0) hints = [];
    if (state.list === pagedNow && state.hints === hints) return;
    state = { list: pagedNow, hints };
    for (const listener of [...listeners]) listener();
  }
  list.subscribe(publish);

  /** Applies `update` to the entry with this id, wherever it is (a hint or a listed entry). */
  function mapEntry(requestId: string, update: (entry: IssuedEntry) => IssuedEntry): void {
    if (hints.some((e) => e.requestId === requestId)) {
      hints = hints.map((e) => (e.requestId === requestId ? update(e) : e));
      publish();
    }
    list.patch(requestId, update);
  }

  function find(requestId: string): IssuedEntry | undefined {
    return hints.find((e) => e.requestId === requestId) ?? list.getState().items.find((e) => e.requestId === requestId);
  }

  async function fetchOne(requestId: string): Promise<void> {
    const entry = find(requestId);
    if (entry === undefined) return;
    const epoch = deps.getEpoch();
    let body: unknown;
    try {
      if (entry.kind !== 'set') {
        if (deps.getTask === undefined || entry.taskId === null) throw new Error('The task could not be loaded.');
        body = await deps.getTask(entry.taskId);
      } else {
        body = await deps.getSet(requestId);
      }
    } catch (thrown) {
      if (epoch !== deps.getEpoch()) return;
      if (thrown !== null && typeof thrown === 'object' && (thrown as { kind?: unknown }).kind === 'stale') return;
      mapEntry(requestId, (e) => entryFailed(e, thrown));
      return;
    }
    if (epoch !== deps.getEpoch()) return;
    mapEntry(requestId, (e) => (e.kind !== 'set' ? entryTaskLoaded(e, body) : entryLoaded(e, body)));
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    ensureLoaded: () => list.ensureLoaded(),
    load: () => list.load(),
    loadMore: () => list.loadMore(),
    async track(response, title, meta) {
      const step = addHint(hints, response, title, meta);
      if (step.entry === null) return;
      // The read of the server list starts FIRST (it publishes `loading` at once), so the hint added next is not dropped by the previous `ready`.
      const reading = list.load();
      hints = step.hints;
      publish();
      await reading;
      // The server list could not be read (an older server, or a failure): the hint stays, and its figure is read through the set or task route.
      const phase = list.getState().phase;
      if (phase === 'unavailable' || phase === 'error') await fetchOne(step.entry.requestId);
    },
    async refresh(requestId) {
      mapEntry(requestId, entryRefreshing);
      await fetchOne(requestId);
    },
    refreshAll: () => list.load(),
    async applyChange(requestId, body) {
      const entry = find(requestId);
      if (entry === undefined) return;
      if (entry.kind === 'set' && parseSetAnswer(body) !== null) {
        mapEntry(requestId, (e) => entryLoaded(e, body));
        return;
      }
      mapEntry(requestId, entryRefreshing);
      await fetchOne(requestId);
    },
    async callerChanged() {
      hints = [];
      await list.callerChanged();
      publish();
    },
  };
}
